import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { BackButton } from "../../../../components/buttons/backButton";
import { Button } from "../../../../components/buttons/button";
import { ProfileIcon } from "../../../../components/cards/profilePic";
import { Icon } from "../../../../components/icon/icon";
import { AdminEditUserModal } from "../../../../components/modal/app_modals/adminEditUserModal";
import { ConfirmationModal } from "../../../../components/modal/app_modals/confirmationModal";
import { SelectSalesAgentModal } from "../../../../components/modal/app_modals/selectSalesAgentModal";
import { Pill } from "../../../../components/pill";
import { TabNav } from "../../../../components/tabNav";
import { MiniHeader } from "../../../../components/typography/miniHeader";
import { BgWrapper } from "../../../../components/wrapper/bgWrapper";
import { useModalControl } from "../../../../customeHooks/useModalControl";
import { useAssignUserToAgentMutation } from "../../../../redux/features/admin/sales/sales.api";
import {
  useGetSingleUserQuery,
  useToggleUserSuspensionMutation,
} from "../../../../redux/features/admin/users/users.api";
import { PAYSTACK_URL, userStatusPillMap } from "../../../../utils/constants";
import { formatDate } from "../../../../utils/helpers";
import { UserStatus } from "../../../../utils/types";
import { SingleUserDetails } from "./details";
import { SingleUserEvents } from "./events";

export const SingleUserPage = () => {
  const [view, setView] = useState<"details" | "events">("details");
  const [isPayoutAccVerified, setIsPayoutAccVerified] = useState(false);

  const { id } = useParams<{ id: string }>();

  const { modal, modalOpen, handleModalOpen, handleModalClose } =
    useModalControl();

  const {
    data: userData,
    isFetching: fetchingUserData,
    refetch: refetchUserDetails,
  } = useGetSingleUserQuery(
    {
      id: id ?? "",
    },
    {
      refetchOnMountOrArgChange: true,
      skip: !id,
    },
  );

  const [toggleUserSuspension, { isLoading: togglingSuspension }] =
    useToggleUserSuspensionMutation();

  const [assignAgent, { isLoading: assigningAgent }] =
    useAssignUserToAgentMutation();

  useEffect(() => {
    setIsPayoutAccVerified(!!userData?.data?.subAccountIsVerified);
  }, [userData]);

  return (
    <div className="mx-auto flex w-full max-w-[700px] flex-col gap-[2px]">
      <BackButton />

      <BgWrapper
        className="mt-[6px]"
        loading={fetchingUserData}
        loaderHeight={202}
      >
        <div className="flex flex-wrap gap-5 py-5">
          <ProfileIcon dimension={64} className="!p-3" />

          <div className="flex-1">
            <p className="text-2xl font-bold text-grey-900">
              {userData?.data?.fullName}
            </p>
            <div className="mt-[2px] flex items-center gap-1">
              <Icon svg="email-plain" width={16} height={16} className="mb-1" />
              <p className="text-base font-bold text-grey-500">
                {userData?.data?.email}
              </p>
            </div>
            <div className="mt-[2px] flex items-center gap-1">
              <Icon svg="phone-plain" width={16} height={16} className="mb-1" />
              <p className="text-base font-bold text-grey-500">
                {userData?.data?.phoneNumber || "No phone number"}
              </p>
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            <div className="flex flex-wrap gap-2">
              <Pill
                text={
                  userData?.data?.userType === "organiser"
                    ? "ORGANIZER"
                    : "GUEST"
                }
                variation={
                  userData?.data?.userType === "organiser" ? "purple" : "blue"
                }
              />
            </div>
            <Pill
              text={
                userStatusPillMap[
                  userData?.data?.userStatus?.toLowerCase()! as UserStatus
                ]?.text
              }
              variation={
                userStatusPillMap[
                  userData?.data?.userStatus?.toLowerCase()! as UserStatus
                ]?.variant
              }
            />
          </div>
        </div>

        {userData?.data?.userStatus === "suspended" && (
          <div className="mb-5 flex w-full items-center gap-2.5 rounded-[14px] bg-suspend p-3">
            <Icon svg="circle-alert" width={20} height={20} className="mb-1" />
            <p className="text-sm font-bold text-primary">
              Suspended on {formatDate(userData.data.suspendedDate)}
            </p>
          </div>
        )}

        <div className="flex flex-wrap items-center gap-[10px]">
          <Button
            text="Edit"
            icon="pencil"
            type="button"
            onClick={() => {
              handleModalOpen("adminEditUserModal");
            }}
            variation="plain-grey"
            className="max-w-fit"
          />

          <Button
            text={
              userData?.data.userStatus === "suspended"
                ? "Reactivate"
                : "Suspend"
            }
            icon={
              userData?.data.userStatus === "suspended"
                ? "circle-play"
                : "circle-pause"
            }
            type="button"
            onClick={() => {
              handleModalOpen("toggleSuspendModal");
            }}
            variation="plain-grey"
            className="max-w-fit"
          />

          {!isPayoutAccVerified && userData?.data?.userType === "organiser" && (
            <Button
              text="Verify PA"
              icon="share"
              type="button"
              onClick={() => {
                window.open(PAYSTACK_URL, "_blank", "noopener,noreferrer");
              }}
              variation="plain-grey"
              className="max-w-fit"
            />
          )}

          <Button
            text={
              userData?.data?.agent &&
              (Object.keys(userData?.data?.agent)?.length ?? 0) > 0
                ? "Reassign Sales Agent"
                : "Assign Sales Agent"
            }
            icon="circle-plus"
            type="button"
            onClick={() => {
              handleModalOpen("assignSalesAgentModal");
            }}
            variation="plain-grey"
            className="max-w-fit"
          />
        </div>
      </BgWrapper>

      <BgWrapper>
        <TabNav
          navs={[
            {
              title: "Details",
              to: "details",
              onClick: () => {
                setView("details");
              },
            },
            {
              title: "Events",
              to: "events",
              onClick: () => {
                setView("events");
              },
            },
          ]}
          variant="switcher"
          activeTab={view}
        />
      </BgWrapper>

      {view === "details" && (
        <SingleUserDetails
          user={userData}
          goToEvents={() => {
            setView("events");
          }}
          loading={fetchingUserData}
          isPayoutAccVerified={isPayoutAccVerified}
          onDetailsEdit={() => {
            handleModalOpen("adminEditUserModal");
          }}
        />
      )}

      {view === "events" && (
        <SingleUserEvents
          user={userData}
          goToEvents={() => {
            setView("events");
          }}
          userId={id}
        />
      )}

      <ConfirmationModal
        open={modalOpen && modal === "toggleSuspendModal"}
        handleClose={() => {
          handleModalClose();
        }}
        proceedText={
          userData?.data?.userStatus === "suspended" ? "Reactivate" : "Suspend"
        }
        proceedVariation={
          userData?.data?.userStatus === "suspended" ? "primary" : "danger"
        }
        loading={togglingSuspension}
        onProceed={async () => {
          const resp = await toggleUserSuspension({
            id: id ?? "",
            data: {
              action:
                userData?.data?.userStatus === "suspended"
                  ? "unsuspend"
                  : "suspend",
            },
          });

          if (resp.data?.status === "success") {
            handleModalClose();
            refetchUserDetails();
          }
        }}
      >
        <Icon
          svg={
            userData?.data?.userStatus === "suspended"
              ? "circle-play"
              : "triangle-alert"
          }
          width={42}
          height={42}
          className="rounded-xl border border-grey-100 p-[11px]"
        />

        <div className="mb-4 mt-3">
          <MiniHeader
            header={
              userData?.data?.userStatus === "suspended"
                ? "Reactivate User"
                : "Suspend User"
            }
            subheader={
              userData?.data?.userStatus === "suspended"
                ? "Activating this user removes the suspension and allows them log in to their account and perform all actions."
                : "Once you suspend this user, they would not be able to log in to their account until you remove the suspension."
            }
          />
        </div>
      </ConfirmationModal>

      {id && (
        <AdminEditUserModal
          open={modal === "adminEditUserModal" && modalOpen}
          handleClose={() => {
            handleModalClose();
          }}
          user={userData}
          userId={id}
          onSuccess={() => {
            refetchUserDetails();
          }}
        />
      )}

      {id ? (
        <SelectSalesAgentModal
          open={modal === "assignSalesAgentModal" && modalOpen}
          handleClose={() => {
            handleModalClose();
          }}
          onSuccess={async (agents) => {
            const resp = await assignAgent({
              agentId: agents[0]._id,
              organiserId: id,
            });

            if (resp.data?.status === "success") {
              refetchUserDetails();
              handleModalClose();
            }
          }}
          loading={assigningAgent}
          proceedText={
            userData?.data?.agent &&
            (Object.keys(userData?.data?.agent)?.length ?? 0) > 0
              ? "Reassign Sales Agent"
              : "Assign Sales Agent"
          }
          initialSelected={userData?.data?.agent ? [userData.data.agent] : []}
          maxSelection={1}
        />
      ) : undefined}
    </div>
  );
};
